import { SphereDropdownMenuItem } from "@components/common/dropdown/sphere-dropdown-selector-item";
import { SphereDropdownSelectorProps } from "@components/common/dropdown/sphere-dropdown-selector-types";
import { blue } from "@flat-ui";
import {
  DEBOUNCE_DELAY_MS,
  useDebouncedCallback,
} from "@hooks/use-debounce-callback";
import { Box, Fade, Menu, Stack } from "@mui/material";
import { useMemo, useState, useRef } from "react";

/** MAX menu height and create scroll after that */
export const MENU_MAX_HEIGHT = 250;

/** Renders a dropdown with selectable items */
export function SphereDropdownSelector<T extends string | number>({
  items,
  currentValue,
  handleChange,
  defaultLabel,
  sxItem,
  sxDescription,
  sxMenu,
  menuPosition,
  menuMaxHeight = MENU_MAX_HEIGHT,
  menuWidth = "7.875rem",
  transition = Fade,
  renderButton,
}: SphereDropdownSelectorProps<T>): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const isOpen = Boolean(anchorEl);

  const debouncedHandleChange = useDebouncedCallback(
    handleChange,
    DEBOUNCE_DELAY_MS,
    { edges: ["leading"] }
  );

  const currentValueLabel = useMemo(() => {
    const currentValueItem = items.find((item) => item.value === currentValue);
    return currentValueItem?.label;
  }, [currentValue, items]);

  function onValueChange(value: T): void {
    debouncedHandleChange(value);
    setAnchorEl(null);
  }

  function handleOpen(): void {
    if (buttonRef.current) {
      setAnchorEl(isOpen ? null : buttonRef.current);
    }
  }

  const label = currentValueLabel || defaultLabel;

  return (
    <Stack height="100%" justifyContent="center">
      <Box ref={buttonRef}>
        {renderButton({
          isOpen,
          onClick: handleOpen,
          label,
        })}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          debouncedHandleChange.cancel();
          setAnchorEl(null);
        }}
        anchorOrigin={
          menuPosition?.anchorOrigin ?? {
            vertical: "bottom",
            horizontal: "right",
          }
        }
        transformOrigin={
          menuPosition?.transformOrigin ?? {
            vertical: "top",
            horizontal: "right",
          }
        }
        slotProps={{
          paper: {
            sx: {
              minWidth: menuWidth,
              maxHeight: menuMaxHeight,
              overflowY: "auto",
            },
          },
        }}
        sx={{
          "& .MuiMenuItem-root": {
            marginX: "0.5rem",
            "&:hover": {
              color: blue[500],
            },
            minHeight: "fit-content",
          },
          ...sxMenu,
        }}
        slots={{ transition }}
      >
        {items.map((item) => (
          <SphereDropdownMenuItem
            key={item.value}
            item={item}
            currentValue={currentValue}
            handleChange={onValueChange}
            sxItem={sxItem}
            sxDescription={sxDescription}
          />
        ))}
      </Menu>
    </Stack>
  );
}
